import usePathnameWithNoLangCode from 'hooks/usePathnameWithNoLangCode';
import { Role } from 'model/IChat';
import { signOut } from 'modules/authentication/helpers/auth';
import React from 'react';
import AdminMenuItems from 'reusable-components/navigation/NavigationMenuList/AdminMenuItems';
import { hasAnyOfRoles } from 'utils/AccountRoles';
import { isCordova } from 'utils/platform';
import { BUY_A_GIFT_VOUCHER_LINK } from '~/constants';
import CurrencyMenuItem from '~/reusable-components/navigation/NavigationMenuList/CurrencyMenuItem';
import LanguageMenuItem from '~/reusable-components/navigation/NavigationMenuList/LanguageMenuItem';
import MenuDivider from '~/reusable-components/navigation/NavigationMenuList/MenuDivider';
import MenuItem from '~/reusable-components/navigation/NavigationMenuList/MenuItem';
import MenuItemDecorator from '~/reusable-components/navigation/NavigationMenuList/MenuItemDecorator';
import MenuLabel from '~/reusable-components/navigation/NavigationMenuList/MenuLabel';
import VersionNumber from '~/reusable-components/navigation/NavigationMenuList/VersionNumber';
import { useDebugModeStore } from '~/stores/debug';

import DebugMenuItems from './DebugMenuItems';

interface NavigationMenuListProps {
  roles: Role[];
  onLinkClick?: () => void;
}

function NavigationMenuList({ roles, onLinkClick }: NavigationMenuListProps) {
  const pathname = usePathnameWithNoLangCode();
  const debugModeStore = useDebugModeStore();
  const onClickSpread = onLinkClick ? { onClick: onLinkClick } : {};

  const onLogOut = () => {
    void signOut(window.location.href);
  };

  return (
    <>
      {!hasAnyOfRoles(roles, 'host') && (
        <MenuItem
          text="Home"
          url="/"
          {...onClickSpread}
          isActive={pathname === '/'}
          isExternalLink={
            process.env.NODE_ENV !== 'development' && !isCordova()
          }
        />
      )}
      {hasAnyOfRoles(roles, 'host') && (
        <MenuItem
          text="Dashboard"
          url="/host/home/"
          {...onClickSpread}
          isActive={pathname === '/host/home/'}
        />
      )}
      {hasAnyOfRoles(roles, ['onboarding_host', 'onboarding_host_webinar']) && (
        <MenuItem
          isExternalLink
          url={`${BECOME_A_HOST_HOST}/overview`}
          text="Onboarding"
          {...onClickSpread}
        />
      )}
      <MenuItem
        text="Search"
        testId={'MenuItem-Search'}
        url="/search/"
        {...onClickSpread}
        isActive={pathname === '/search/'}
        openModal={!isCordova()}
      />
      {hasAnyOfRoles(roles, 'unlogged') && (
        <>
          <MenuDivider />
          <MenuLabel>Profile</MenuLabel>
          <MenuItem
            text="Log in"
            addReturnTo={true}
            url={`/login/`}
            {...onClickSpread}
            openModal={!isCordova()}
          />
          <MenuItem
            text="Sign up"
            addReturnTo={true}
            url={`/signup/`}
            {...onClickSpread}
            openModal={!isCordova()}
          />
        </>
      )}
      {hasAnyOfRoles(roles, 'loggedin') && (
        <>
          <MenuDivider />
          <MenuLabel>Manage</MenuLabel>
          <MenuItemDecorator link="/messages/">
            <MenuItem
              text="Chat"
              url="/messages/"
              {...onClickSpread}
              isActive={pathname.startsWith('/messages/')}
            />
          </MenuItemDecorator>
          <MenuItem
            text="Bookings"
            url="/schedule/"
            {...onClickSpread}
            isActive={pathname.startsWith('/schedule/')}
          />
          <MenuItem
            text="Favorites"
            url="/favorites/"
            {...onClickSpread}
            isActive={pathname === '/favorites/'}
          />
        </>
      )}
      {hasAnyOfRoles(roles, 'host') && (
        <>
          <MenuDivider />
          <MenuLabel>Manage</MenuLabel>
          <MenuItem
            text="Agenda"
            url="/host-agenda/"
            {...onClickSpread}
            isActive={pathname.startsWith('/host-agenda/')}
          />
          <MenuItem
            text="Finance information"
            url={`${URL_HOST_FINANCES_HOST}/`}
            {...onClickSpread}
            isExternalLink
          />
          <MenuItem
            text="Experiences"
            url="/host/experiences/"
            {...onClickSpread}
            isActive={pathname === '/host/experiences/'}
          />
        </>
      )}
      {hasAnyOfRoles(roles, ['admin', 'impersonator']) && (
        <>
          <MenuDivider />
          <MenuLabel>Admin actions</MenuLabel>
          <MenuItem
            text="Admin / Kebab"
            url={ADMIN_HOST}
            {...onClickSpread}
            isExternalLink
          />
          <AdminMenuItems
            onLinkClick={onLinkClick || (() => {})}
            roles={roles}
          />
        </>
      )}
      {hasAnyOfRoles(roles, 'loggedin') && (
        <>
          <MenuDivider />
          <MenuLabel>Profile</MenuLabel>
          {hasAnyOfRoles(roles, [
            'host',
            'onboarding_host',
            'onboarding_host_webinar',
          ]) ? (
            <MenuItem
              text="Edit profile"
              url="/host/profile/"
              {...onClickSpread}
              isActive={pathname === '/host/profile/'}
            />
          ) : (
            hasAnyOfRoles(roles, 'guest') && (
              <MenuItem
                text="Edit profile"
                url={`${URL_HOST}/account/`}
                {...onClickSpread}
                isExternalLink
              />
            )
          )}
          <MenuItem
            text="Change password"
            url={`${URL_STOREFRONT_HOST}/password/`}
            {...onClickSpread}
            isExternalLink
          />
        </>
      )}
      <MenuDivider />
      <MenuLabel>Support</MenuLabel>
      <MenuItem
        text="Buy a gift voucher"
        url={BUY_A_GIFT_VOUCHER_LINK}
        {...onClickSpread}
        isExternalLink
        openInNewTab
        noAuth
      />
      {!hasAnyOfRoles(roles, 'host') && (
        <MenuItem
          text="Become a host"
          url={`${URL_HOST}/become-a-host/`}
          {...onClickSpread}
          isExternalLink
        />
      )}
      {hasAnyOfRoles(roles, 'host') && (
        <MenuItem
          text="Learning center"
          url={`/learning-center/`}
          {...onClickSpread}
          isActive={pathname === '/learning-center/'}
        />
      )}
      <MenuItem
        text="Consent"
        url={`${!isCordova() ? URL_STOREFRONT_HOST : ''}/consent/`}
        {...onClickSpread}
        isExternalLink={isCordova() === false}
      />
      <MenuItem
        text="Privacy policy"
        url="/info/privacy-policy/"
        {...onClickSpread}
        isActive={pathname === '/info/privacy-policy/'}
      />
      <MenuItem
        text="Help"
        url="https://support.withlocals.com/hc/en-us"
        {...onClickSpread}
        isExternalLink
        openInNewTab
        noAuth
      />
      <MenuDivider />
      <CurrencyMenuItem />
      <LanguageMenuItem {...onClickSpread} />
      {hasAnyOfRoles(roles, 'loggedin') && (
        <>
          <MenuDivider />
          <MenuItem text="Log out" onClick={onLogOut} addReturnTo={true} />
        </>
      )}
      <VersionNumber />
      {debugModeStore.debugMode && (
        <>
          <MenuLabel>Debug</MenuLabel>
          <DebugMenuItems />
        </>
      )}
    </>
  );
}

export default NavigationMenuList;
